/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import LayerConfig from '@/models/map-context/LayerConfig';

declare let Cesium: any;

const MAX_URL_LENGTH = 2000;

interface WmsPostContext {
  layerConfig: LayerConfig;
  csrfToken: string;
  getProxyFiedUrl: (url: string) => string;
}

function needsPost(fullUrl: string): boolean {
  return fullUrl.length >= MAX_URL_LENGTH;
}

function buildPostHeaders(ctx: WmsPostContext): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/x-www-form-urlencoded',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };
  if (ctx.csrfToken) {
    headers['X-CSRFToken'] = ctx.csrfToken;
  }
  if (ctx.layerConfig.authenticationInfos.isFilled()) {
    headers.Authorization = ctx.layerConfig.authenticationInfos.getBasicAutorisationValue();
  }
  return headers;
}

function splitWmsUrl(fullUrl: string, ctx: WmsPostContext): { baseUrl: string; postBody: string } {
  const [base, postBody = ''] = fullUrl.split('?');
  return {
    baseUrl: ctx.layerConfig.useProxy ? ctx.getProxyFiedUrl(base) : base,
    postBody,
  };
}

function isRequestCancelled(request: any): boolean {
  return !!request && (
    request.cancelled
    || request.state === Cesium.RequestState.CANCELLED
  );
}

function when<T>(promise: Promise<T>): any {
  return Cesium.when(promise);
}

function cancelPreviousTileRequest(
  activeTileRequests: Map<string, any>,
  x: number,
  y: number,
  level: number,
  request: any,
): void {
  const key = `${level}/${x}/${y}`;
  const previous = activeTileRequests.get(key);
  if (previous && previous !== request) {
    previous.cancelFunction?.();
    previous.cancelled = true;
  }
  activeTileRequests.set(key, request);
}

function xhrPost(
  baseUrl: string,
  postBody: string,
  headers: Record<string, string>,
  request: any,
): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', baseUrl, true);
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.responseType = 'arraybuffer';

    const fail = (message: string) => reject(new Error(message));

    xhr.onload = () => {
      if (isRequestCancelled(request)) {
        fail('Request cancelled');
      } else if (xhr.status === 0) {
        fail('Request aborted');
      } else if (xhr.status >= 400) {
        fail(`WMS POST request failed (${xhr.status}): ${baseUrl}`);
      } else {
        resolve(xhr.response);
      }
    };
    xhr.onerror = () => fail(`WMS POST request failed: ${baseUrl}`);
    xhr.onabort = () => fail('Request aborted');

    request.cancelFunction = () => xhr.abort();
    xhr.send(postBody);
  });
}

function getWmsErrorFromResponse(response: ArrayBuffer): string | null {
  const bytes = new Uint8Array(response);
  if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return null;
  }
  const str = new TextDecoder().decode(response);
  return str.indexOf('ServiceException') >= 0 ? str : null;
}

function schedulePostRequest(
  request: any,
  fullUrl: string,
  ctx: WmsPostContext,
): any {
  const { baseUrl, postBody } = splitWmsUrl(fullUrl, ctx);
  const headers = buildPostHeaders(ctx);

  request.url = fullUrl;
  request.requestFunction = () => when(xhrPost(baseUrl, postBody, headers, request));

  return Cesium.RequestScheduler.request(request);
}

function postResourceArrayBuffer(resource: any, fullUrl: string, ctx: WmsPostContext): any {
  const responsePromise = schedulePostRequest(resource.request, fullUrl, ctx);
  if (!responsePromise) {
    return undefined;
  }

  return responsePromise.then((response: ArrayBuffer) => {
    if (isRequestCancelled(resource.request)) {
      return undefined;
    }
    return response;
  });
}

function postResourceText(resource: any, fullUrl: string, ctx: WmsPostContext): any {
  return postResourceArrayBuffer(resource, fullUrl, ctx)
    ?.then((response: ArrayBuffer) => new TextDecoder().decode(response));
}

function arrayBufferToImageBitmap(response: ArrayBuffer, flipY: boolean): any {
  const error = getWmsErrorFromResponse(response);
  if (error) {
    throw new Error(`Erreur WMS: ${error}`);
  }

  const blob = new Blob([new Uint8Array(response)], { type: 'image/png' });
  return when(Cesium.Resource.createImageBitmapFromBlob(blob, {
    flipY,
    premultiplyAlpha: false,
    skipColorSpaceConversion: false,
  }));
}

function patchResourceFetchImageForPost(resource: any, fullUrl: string, ctx: WmsPostContext): void {
  resource.fetchImage = function fetchImageViaPost(options: any) {
    const resolvedOptions = Cesium.defaultValue(options, Cesium.defaultValue.EMPTY_OBJECT);
    const flipY = Cesium.defaultValue(resolvedOptions.flipY, false);

    const responsePromise = postResourceArrayBuffer(this, fullUrl, ctx);
    if (!responsePromise) {
      return undefined;
    }

    return responsePromise.then((response: ArrayBuffer) => {
      if (!response) {
        return undefined;
      }
      return arrayBufferToImageBitmap(response, flipY);
    });
  };
}

function interceptResourceFetchMethods(ctx: WmsPostContext, callback: () => any): any {
  const resourceProto = Cesium.Resource.prototype;
  const originals = {
    fetchXML: resourceProto.fetchXML,
    fetchText: resourceProto.fetchText,
    fetchJson: resourceProto.fetchJson,
  };

  const withPost = (
    original: (...args: any[]) => any,
    transform?: (text: string) => any,
  ) => function patchedFetch(this: any) {
    const fullUrl = this.getUrlComponent(true);
    if (!needsPost(fullUrl)) {
      return original.call(this);
    }
    const textPromise = postResourceText(this, fullUrl, ctx);
    return transform ? textPromise?.then(transform) : textPromise;
  };

  resourceProto.fetchXML = withPost(originals.fetchXML, (text) => new DOMParser().parseFromString(text, 'text/xml'));
  resourceProto.fetchText = withPost(originals.fetchText);
  resourceProto.fetchJson = withPost(originals.fetchJson, (text) => JSON.parse(text));

  try {
    return callback();
  } finally {
    resourceProto.fetchXML = originals.fetchXML;
    resourceProto.fetchText = originals.fetchText;
    resourceProto.fetchJson = originals.fetchJson;
  }
}

export function patchWmsImageryProviderForPost(
  wmsProvider: any,
  layerConfig: LayerConfig,
  csrfToken: string,
  getProxyFiedUrl: (url: string) => string,
): void {
  layerConfig.checkSourceAuthentification();

  const ctx: WmsPostContext = { layerConfig, csrfToken, getProxyFiedUrl };
  const tileProvider = wmsProvider._tileProvider;
  const originalTileRequestImage = tileProvider.requestImage.bind(tileProvider);
  const originalPickFeatures = tileProvider.pickFeatures.bind(tileProvider);
  const activeTileRequests = new Map<string, any>();

  tileProvider.requestImage = (x: number, y: number, level: number, request?: any) => {
    if (!tileProvider.ready) {
      return originalTileRequestImage(x, y, level, request);
    }

    const originalLoadImage = Cesium.ImageryProvider.loadImage;

    Cesium.ImageryProvider.loadImage = function loadImageWithPostSupport(provider: any, url: any) {
      Cesium.ImageryProvider.loadImage = originalLoadImage;

      const resource = Cesium.Resource.createIfNeeded(url);
      const fullUrl = resource.getUrlComponent(true);

      if (!needsPost(fullUrl)) {
        return originalLoadImage(provider, url);
      }

      if (request) {
        cancelPreviousTileRequest(activeTileRequests, x, y, level, request);
      }

      patchResourceFetchImageForPost(resource, fullUrl, ctx);
      return resource.fetchImage({ preferImageBitmap: true, flipY: true });
    };

    try {
      return originalTileRequestImage(x, y, level, request);
    } finally {
      Cesium.ImageryProvider.loadImage = originalLoadImage;
    }
  };

  tileProvider.pickFeatures = (
    x: number,
    y: number,
    level: number,
    longitude: number,
    latitude: number,
  ) => interceptResourceFetchMethods(ctx, () => originalPickFeatures(x, y, level, longitude, latitude));
}
